"use client";

import { useState, useEffect, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import Icon from "@/components/ui/AppIcon";
import Header from "@/components/Header";
import AccountCreationForm from "./components/AccountCreationForm";
import BillingForm from "./components/BillingForm";
import OrderSummary from "./components/OrderSummary";
import TrustBadges from "./components/TrustBadges";

type Step = "account" | "billing" | "payment";

const steps: { id: Step; label: string; icon: string }[] = [
  { id: "account", label: "Account", icon: "UserCircleIcon" },
  { id: "billing", label: "Billing", icon: "DocumentTextIcon" },
  { id: "payment", label: "Payment", icon: "CreditCardIcon" },
];

function CheckoutContent() {
  const searchParams = useSearchParams();
  const planId = searchParams.get("plan") || "starter-kit";
  const token = searchParams.get("token"); // Auto-login token from upgrade link
  const emailParam = searchParams.get("email"); // Email from upgrade link
  const isFree = planId === "free";
  const [loginToken, setLoginToken] = useState<string | null>(token);

  const [currentStep, setCurrentStep] = useState<Step>("account");
  const [completedSteps, setCompletedSteps] = useState<Set<Step>>(new Set());
  const [accountData, setAccountData] = useState<{
    email: string;
    name: string;
    password: string;
    method: string;
    isSignIn?: boolean;
    userId?: number;
    currentPlan?: string;
    tagsQuota?: number;
  } | null>(null);
  const [billingData, setBillingData] = useState<{ name: string; country: string } | null>(null);
  const [isGuest, setIsGuest] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [paymentProvider, setPaymentProvider] = useState<"stripe" | "przelewy24">("stripe");
  const [autoLoggingIn, setAutoLoggingIn] = useState(false);

  // Plan prices in cents
  const planPrices: Record<string, number> = {
    "starter-kit": 4900,
    "starter": 900,
    "pro": 1900,
    "collector": 4900,
  };

  const planPrice = planPrices[planId] || 4900;
  const planPriceFormatted = (planPrice / 100).toFixed(2);

  // For free plans, only show account step
  const activeSteps = isFree ? [steps[0]] : steps;
  const stepIndex = activeSteps.findIndex(s => s.id === currentStep);
  const progressPercent = Math.round(((completedSteps.size) / activeSteps.length) * 100);

  // Auto-login if token is present (from upgrade link)
  useEffect(() => {
    if (token && emailParam && !autoLoggingIn) {
      setAutoLoggingIn(true);
      // Mark account as complete and proceed to billing with actual email
      setAccountData({
        email: emailParam,
        name: emailParam.split('@')[0], // Use email prefix as name
        password: '',
        method: 'token',
        isSignIn: true
      });
      setCompletedSteps(new Set(['account']));
      setCurrentStep('billing');
    }
  }, [token, emailParam, autoLoggingIn]);

  const handleAccountComplete = async (data: {
    email: string;
    name: string;
    password: string;
    method: string;
    isSignIn?: boolean;
    userId?: number;
    currentPlan?: string;
    tagsQuota?: number;
  }) => {
    setAccountData(data);
    setCompletedSteps(prev => new Set([...prev, "account"]));

    // If signing in with existing account
    if (data.isSignIn && data.userId) {
      // Check if user already has a paid plan
      if (data.currentPlan && data.currentPlan !== 'free') {
        setError(`You already have the ${data.currentPlan} plan. Please contact support to change plans.`);
        setLoading(false);
        return;
      }

      // Free plan selected but user wants to upgrade - redirect to cloud
      if (planId === "free") {
        setLoading(true);
        try {
          const cloudServiceUrl = process.env.NEXT_PUBLIC_CLOUD_SERVICE_URL || 'http://localhost:5000';
          const response = await fetch(`${cloudServiceUrl}/api/auto-login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: data.email,
              password: data.password,
            }),
          });

          const result = await response.json();

          if (!response.ok) {
            throw new Error(result.error || 'Auto-login failed');
          }

          // Redirect to auto-login URL
          window.location.href = result.login_url;
        } catch (err: any) {
          console.error('Auto-login error:', err);
          setError(err.message || 'Login failed. Please try again.');
          setLoading(false);
        }
        return;
      }

      // User is upgrading from free to paid - continue to billing
      setCurrentStep("billing");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Creating new account
    if (planId === "free") {
      await handleFreeSignup({ name: data.name, country: "" }, data);
      return;
    }

    setCurrentStep("billing");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleGuestContinue = () => {
    setIsGuest(true);
    setCompletedSteps(prev => new Set([...prev, "account"]));
    setCurrentStep("billing");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBillingComplete = async (data: { name: string; country: string }) => {
    setBillingData(data);
    setCompletedSteps(prev => new Set([...prev, "billing"]));

    // Free plan - skip payment and create account directly
    if (planId === "free") {
      await handleFreeSignup(data);
      return;
    }

    setCurrentStep("payment");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFreeSignup = async (billing: { name: string; country: string }, account?: { email: string; name: string; password: string; method: string }) => {
    setLoading(true);
    setError("");

    // Use provided account data or fall back to state
    const accountToUse = account || accountData;

    try {
      // Call our server-side API (which then calls cloud service)
      const response = await fetch('/api/signup/free', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: accountToUse?.email || '',
          name: billing.name || accountToUse?.name || '',
          password: accountToUse?.password || '',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Account creation failed');
      }

      // Redirect to success page
      window.location.href = `/payment/success?plan=free&email=${encodeURIComponent(accountToUse?.email || '')}`;
    } catch (err: any) {
      console.error('Free signup error:', err);
      setError(err.message || 'Account creation failed. Please try again.');
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    setError("");

    try {
      if (paymentProvider === "stripe") {
        // Stripe Checkout
        const response = await fetch("/api/payment/stripe/checkout", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: accountData?.email || "",
            name: billingData?.name || accountData?.name || "",
            plan: planId,
            userId: accountData?.userId, // Include userId for upgrades
            loginToken: loginToken, // Pass token for auto-login after payment
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Payment initiation failed");
        }

        // Redirect to Stripe Checkout
        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        } else {
          throw new Error("No checkout URL received");
        }
      } else {
        // Przelewy24 Checkout
        const response = await fetch("/api/payment/initiate", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: accountData?.email || "",
            name: billingData?.name || accountData?.name || "",
            plan: planId,
            amount: planPrice,
            userId: accountData?.userId, // Include userId for upgrades
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Payment initiation failed");
        }

        // Redirect to Przelewy24 payment page
        if (data.paymentUrl) {
          window.location.href = data.paymentUrl;
        } else {
          throw new Error("No payment URL received");
        }
      }
    } catch (err: any) {
      console.error("Payment error:", err);
      setError(err.message || "Payment initiation failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary">
      {/* Standard Header */}
      <Header />

      {/* Progress Bar */}
      <div className="bg-bg-secondary/30 border-b border-white/5 pt-24">
        <div className="max-w-container mx-auto px-6 md:px-8 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-6">
              {activeSteps.map((step, i) => {
                const isCompleted = completedSteps.has(step.id);
                const isCurrent = currentStep === step.id;
                const isReachable = i === 0 || completedSteps.has(activeSteps[i - 1].id);

                return (
                  <button
                    key={step.id}
                    onClick={() => isReachable && setCurrentStep(step.id)}
                    disabled={!isReachable}
                    className={`flex items-center gap-2 transition-all duration-300 ${
                      isCurrent ? "opacity-100" : isCompleted ? "opacity-70 hover:opacity-100" : "opacity-30"
                    } disabled:cursor-not-allowed`}
                    aria-current={isCurrent ? "step" : undefined}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                      isCompleted
                        ? "bg-accent-emerald text-white"
                        : isCurrent
                        ? "bg-accent-crimson text-white" :"bg-white/10 text-gray-500"
                    }`}>
                      {isCompleted ? (
                        <Icon name="CheckIcon" size={13} />
                      ) : (
                        <span>{i + 1}</span>
                      )}
                    </div>
                    <span className={`text-xs font-semibold hidden sm:inline ${
                      isCurrent ? "text-white" : isCompleted ? "text-gray-400" : "text-gray-600"
                    }`}>
                      {step.label}
                    </span>
                  </button>
                );
              })}
            </div>
            <span className="text-xs font-mono text-gray-600">{progressPercent}% complete</span>
          </div>

          {/* Progress track */}
          <div className="h-0.5 bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-accent-crimson to-accent-crimson/60 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-container mx-auto px-6 md:px-8 py-10 md:py-14">
        {/* Page Header */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center gap-2 bg-accent-crimson/10 border border-accent-crimson/20 rounded-full px-4 py-1.5 mb-4">
            <Icon name="ShieldCheckIcon" size={13} className="text-accent-crimson" />
            <span className="text-accent-crimson text-xs font-semibold">Secure checkout powered by Przelewy24</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl text-white mb-2">Complete Your Order</h1>
          <p className="text-gray-500 text-sm">You&apos;re just a few steps away from transforming your collection</p>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8 xl:gap-12">
          {/* Left: Main Content */}
          <div className="space-y-6">
            {/* Step 1: Account */}
            {currentStep === "account" && (
              <div className="animate-fade-in-up">
                <AccountCreationForm
                  onComplete={handleAccountComplete}
                  onGuestContinue={handleGuestContinue}
                />
              </div>
            )}

            {/* Step 2: Billing */}
            {currentStep === "billing" && (
              <div className="animate-fade-in-up">
                {/* Upgrade Notice */}
                {accountData?.isSignIn && accountData?.currentPlan && accountData?.currentPlan === 'free' && !isFree && (
                  <div className="bg-accent-emerald/5 border border-accent-emerald/15 rounded-2xl px-5 py-4 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-accent-emerald/20 border border-accent-emerald/30 flex items-center justify-center">
                        <Icon name="ArrowUpIcon" size={16} className="text-accent-emerald" />
                      </div>
                      <div>
                        <p className="text-accent-emerald text-sm font-semibold">Upgrading Your Account</p>
                        <p className="text-gray-500 text-xs mt-0.5">
                          From Free Plan to {planId === 'starter-kit' ? 'Starter Kit' : 'Pro Plan'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Account summary */}
                {(accountData || isGuest) && (
                  <div className="bg-bg-card border border-white/5 rounded-2xl px-5 py-4 mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-accent-emerald/10 border border-accent-emerald/20 flex items-center justify-center">
                        <Icon name="CheckIcon" size={14} className="text-accent-emerald" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">
                          {isGuest ? "Continuing as guest" : accountData?.email}
                        </p>
                        <p className="text-gray-600 text-xs">
                          {isGuest ? "Account creation optional" : `via ${accountData?.method}`}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setCurrentStep("account")}
                      className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      Edit
                    </button>
                  </div>
                )}
                <BillingForm
                  onComplete={handleBillingComplete}
                  prefillName={accountData?.name || ""}
                />
              </div>
            )}

            {/* Step 3: Payment */}
            {currentStep === "payment" && (
              <div className="animate-fade-in-up">
                <div className="bg-bg-card border border-white/8 rounded-3xl p-6 md:p-8">
                  <div className="mb-6">
                    <h2 className="font-serif text-2xl text-white mb-1">Payment</h2>
                    <p className="text-gray-500 text-sm">Choose your payment method</p>
                  </div>

                  {/* Payment Provider Selector */}
                  <div className="mb-6">
                    <label className="block text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider">
                      Payment Provider
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setPaymentProvider("stripe")}
                        className={`p-4 rounded-2xl border-2 transition-all duration-200 ${
                          paymentProvider === "stripe"
                            ? "border-accent-crimson bg-accent-crimson/5"
                            : "border-white/10 hover:border-white/20"
                        }`}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="text-2xl">💳</div>
                          <span className={`text-sm font-semibold ${
                            paymentProvider === "stripe" ? "text-white" : "text-gray-400"
                          }`}>
                            Stripe
                          </span>
                          <span className="text-[10px] text-gray-600">Credit Card</span>
                        </div>
                      </button>
                      <button
                        onClick={() => setPaymentProvider("przelewy24")}
                        className={`p-4 rounded-2xl border-2 transition-all duration-200 ${
                          paymentProvider === "przelewy24"
                            ? "border-accent-crimson bg-accent-crimson/5"
                            : "border-white/10 hover:border-white/20"
                        }`}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="text-2xl">🏦</div>
                          <span className={`text-sm font-semibold ${
                            paymentProvider === "przelewy24" ? "text-white" : "text-gray-400"
                          }`}>
                            Przelewy24
                          </span>
                          <span className="text-[10px] text-gray-600">Polish Payments</span>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Payment gateway info */}
                  <div className="bg-bg-elevated border border-white/5 rounded-2xl p-8 text-center mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-accent-purple/10 border border-accent-purple/20 flex items-center justify-center mx-auto mb-4">
                      <Icon name="CreditCardIcon" size={26} className="text-accent-purple" />
                    </div>
                    {paymentProvider === "stripe" ? (
                      <>
                        <h3 className="text-white font-semibold mb-2">Stripe Secure Checkout</h3>
                        <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                          You&apos;ll be redirected to Stripe&apos;s secure checkout page.
                          Industry-leading security with millions of businesses worldwide.
                        </p>
                        <div className="flex items-center justify-center gap-3 mb-6 flex-wrap">
                          <div className="bg-white rounded-md px-3 py-1.5">
                            <span className="text-[#1a1f71] font-bold text-sm">VISA</span>
                          </div>
                          <div className="bg-white rounded-md px-2 py-1.5 flex items-center gap-1">
                            <div className="w-5 h-5 rounded-full bg-[#eb001b]" />
                            <div className="w-5 h-5 rounded-full bg-[#f79e1b] -ml-2.5" />
                          </div>
                          <div className="bg-white rounded-md px-3 py-1.5">
                            <span className="text-[#0074de] font-bold text-sm">AMEX</span>
                          </div>
                          <div className="bg-black rounded-md px-3 py-1.5">
                            <span className="text-white font-bold text-xs"> Pay</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <h3 className="text-white font-semibold mb-2">Przelewy24 Secure Payment</h3>
                        <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                          You&apos;ll be redirected to Przelewy24&apos;s secure payment page.
                          Popular Polish payment gateway with BLIK and bank transfers.
                        </p>
                        <div className="flex items-center justify-center gap-3 mb-6 flex-wrap">
                          <div className="bg-white rounded-md px-3 py-1.5">
                            <span className="text-[#1a1f71] font-bold text-sm">VISA</span>
                          </div>
                          <div className="bg-white rounded-md px-2 py-1.5 flex items-center gap-1">
                            <div className="w-5 h-5 rounded-full bg-[#eb001b]" />
                            <div className="w-5 h-5 rounded-full bg-[#f79e1b] -ml-2.5" />
                          </div>
                          <div className="bg-[#e2001a] rounded-md px-3 py-1.5">
                            <span className="text-white font-bold text-sm">BLIK</span>
                          </div>
                          <div className="bg-bg-card border border-white/10 rounded-md px-3 py-1.5">
                            <span className="text-gray-400 text-xs font-mono">Transfer</span>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="flex items-center justify-center gap-2 text-xs text-gray-600 mb-6">
                      <Icon name="LockClosedIcon" size={12} className="text-accent-emerald" />
                      <span>Secure {paymentProvider === "stripe" ? "SSL" : "encrypted"} payment processing</span>
                    </div>
                  </div>

                  {/* Error message */}
                  {error && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-start gap-3 mb-6">
                      <Icon name="ExclamationTriangleIcon" size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  {/* Complete Order CTA */}
                  <button
                    onClick={handlePayment}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-accent-crimson to-accent-crimson-hover text-white font-bold text-base py-5 rounded-full shadow-[0_12px_32px_rgba(255,0,76,0.4)] hover:shadow-[0_20px_48px_rgba(255,0,76,0.5)] hover:-translate-y-1 active:translate-y-0 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-[0_12px_32px_rgba(255,0,76,0.4)]"
                    aria-label="Complete order and pay $49"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <Icon name="LockClosedIcon" size={18} />
                        <span>Complete Order — ${planPriceFormatted}</span>
                        <Icon name="ArrowRightIcon" size={18} />
                      </>
                    )}
                  </button>

                  <p className="text-center text-gray-600 text-xs mt-4">
                    By completing your order you agree to our{" "}
                    <a href="#" className="text-gray-400 hover:text-white underline underline-offset-2 transition-colors">Terms of Service</a>
                    {" "}and{" "}
                    <a href="#" className="text-gray-400 hover:text-white underline underline-offset-2 transition-colors">Privacy Policy</a>
                  </p>
                </div>
              </div>
            )}

            {/* Trust Badges */}
            <TrustBadges />
          </div>

          {/* Right: Order Summary Sidebar */}
          <div className="order-first lg:order-last">
            <OrderSummary
              planId={planId}
              isUpgrade={accountData?.isSignIn && accountData?.currentPlan === 'free'}
              currentPlan={accountData?.currentPlan}
            />
          </div>
        </div>
      </div>

      {/* Mobile sticky footer CTA */}
      {currentStep === "payment" && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 p-4 bg-bg-primary/95 backdrop-blur-xl border-t border-white/5">
          <button
            onClick={handlePayment}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-accent-crimson to-accent-crimson-hover text-white font-bold text-sm py-4 rounded-full shadow-[0_8px_24px_rgba(255,0,76,0.4)] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <Icon name="LockClosedIcon" size={15} />
                <span>Complete Order — ${planPriceFormatted}</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default function CheckoutPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-2 border-accent-crimson border-t-transparent animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Loading checkout...</p>
        </div>
      </div>
    }>
      <CheckoutContent />
    </Suspense>
  );
}
